/**
 * Number Field Component
 *
 * Renders a number input for numeric properties with expression mode support.
 * Users can toggle between fixed numeric value and expression mode.
 * In expression mode, accepts string expressions that evaluate to numbers.
 */

import { useState, useMemo, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import {
  HelpCircle,
  Code2,
  Hash,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface InputData {
  nodeId: string;
  nodeName: string;
  output: any;
  sourceHandle?: string;
  nodeDistance: number;
}

interface NumberFieldProps {
  property: InferredPropertySchema;
  value: number | string;  // Can be number or expression string
  onChange: (value: number | string) => void;
  error?: string;
  inputData?: InputData[];
}

// Helper to detect if value contains expressions
function containsExpression(value: any): boolean {
  return typeof value === 'string' && /\{\{.*?\}\}/.test(value);
}

// Helper to extract numeric fields from an object recursively
function extractNumericFields(obj: any, prefix = ''): { path: string; value: any; type: string }[] {
  const fields: { path: string; value: any; type: string }[] = [];

  if (!obj || typeof obj !== 'object') return fields;

  // Handle arrays
  if (Array.isArray(obj)) {
    if (obj.length > 0 && typeof obj[0] === 'object') {
      const arrayPath = prefix ? `${prefix}[0]` : '[0]';
      fields.push(...extractNumericFields(obj[0], arrayPath));
    }
    return fields;
  }

  Object.keys(obj).forEach((key) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];
    const type = Array.isArray(value)
      ? 'array'
      : value === null
      ? 'null'
      : typeof value;

    // Only add numeric fields for the number picker
    if (type === 'number') {
      fields.push({ path, value, type });
    }

    // Also include strings that look like numbers
    if (type === 'string' && !isNaN(Number(value))) {
      fields.push({ path, value, type: 'number' });
    }

    // Recursively extract nested fields
    if (typeof value === 'object' && value !== null && path.split('.').length < 5) {
      fields.push(...extractNumericFields(value, path));
    }
  });

  return fields;
}

/**
 * Parse a property path into parts, properly handling bracket notation with quoted strings.
 * Examples:
 *   "data.name" -> ["data", "name"]
 *   "childOutputs["AI Agent"]" -> ["childOutputs", "AI Agent"]
 *   "items[0].name" -> ["items", "0", "name"]
 */
function parsePathParts(path: string): string[] {
  if (!path) return [];

  const parts: string[] = [];
  let i = 0;

  while (i < path.length) {
    // Skip dots
    if (path[i] === '.') {
      i++;
      continue;
    }

    // Handle bracket notation
    if (path[i] === '[') {
      const closeBracket = path.indexOf(']', i);
      if (closeBracket === -1) {
        parts.push(path.substring(i));
        break;
      }

      let content = path.substring(i + 1, closeBracket);

      // Strip quotes if present
      if ((content.startsWith('"') && content.endsWith('"')) ||
          (content.startsWith("'") && content.endsWith("'"))) {
        content = content.slice(1, -1);
      }

      parts.push(content);
      i = closeBracket + 1;
      continue;
    }

    // Regular property name
    const start = i;
    while (i < path.length && path[i] !== '.' && path[i] !== '[') {
      i++;
    }

    const propName = path.substring(start, i);
    if (propName) {
      parts.push(propName);
    }
  }

  return parts;
}

// Simple expression evaluator for preview
function evaluateExpression(expr: string, inputData: InputData[]): { value: any; error: string | null } {
  try {
    const matches = expr.match(/\{\{(.*?)\}\}/g);
    if (!matches) return { value: expr, error: null };

    let result = expr;

    for (const match of matches) {
      const expression = match.slice(2, -2).trim();

      // Handle $node["NodeName"].output.field
      const nodeMatch = expression.match(/^\$node\["([^"]+)"\]\.output\.?(.*)$/);
      if (nodeMatch) {
        const [, nodeName, fieldPath] = nodeMatch;
        const nodeData = inputData.find(d => d.nodeName === nodeName);

        if (!nodeData) {
          return { value: null, error: `Node "${nodeName}" not found` };
        }

        let value = nodeData.output;
        // Unwrap unified format if needed: { success, output, logs, metrics }
        if (value && typeof value === 'object' && 'output' in value) {
          value = value.output;
        }
        if (fieldPath) {
          const parts = parsePathParts(fieldPath);
          for (const part of parts) {
            if (value === undefined || value === null) {
              return { value: null, error: `Field "${fieldPath}" not found` };
            }
            value = value[part];
          }
        }

        if (expr === match) {
          return { value, error: null };
        }
        result = result.replace(match, String(value ?? ''));
      }
    }

    return { value: result, error: null };
  } catch (err) {
    return { value: null, error: err instanceof Error ? err.message : 'Invalid expression' };
  }
}

export function NumberField({ property, value, onChange, error, inputData = [] }: NumberFieldProps) {
  const [isExpressionMode, setIsExpressionMode] = useState(() => containsExpression(value));
  const [fieldBrowserOpen, setFieldBrowserOpen] = useState(false);

  // Auto-detect expression mode when value changes
  useEffect(() => {
    if (containsExpression(value) && !isExpressionMode) {
      setIsExpressionMode(true);
    }
  }, [value, isExpressionMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isExpressionMode) {
      // In expression mode, pass through as string
      onChange(e.target.value);
    } else {
      // In fixed mode, parse as number
      const numValue = parseFloat(e.target.value);
      if (!isNaN(numValue)) {
        onChange(numValue);
      } else if (e.target.value === '') {
        onChange('' as any);  // Allow clearing
      }
    }
  };

  // Evaluate expression for preview
  const preview = useMemo(() => {
    if (!isExpressionMode || !containsExpression(value)) {
      return { value: null, error: null };
    }
    return evaluateExpression(String(value), inputData);
  }, [value, inputData, isExpressionMode]);

  // Extract numeric fields from upstream nodes
  const availableFields = useMemo(() => {
    const fields: { nodeName: string; path: string; fullPath: string; value: any; type: string }[] = [];
    const sortedInput = [...inputData].sort((a, b) => a.nodeDistance - b.nodeDistance);

    for (const input of sortedInput) {
      let outputData = input.output;
      if (outputData && typeof outputData === 'object' && 'output' in outputData) {
        outputData = outputData.output;
      }
      if (!outputData) continue;

      const nodeFields = extractNumericFields(outputData);
      for (const field of nodeFields) {
        fields.push({
          nodeName: input.nodeName,
          path: field.path,
          fullPath: `$node["${input.nodeName}"].output.${field.path}`,
          value: field.value,
          type: field.type,
        });
      }
    }

    return fields;
  }, [inputData]);

  // Group fields by node
  const fieldsByNode = useMemo(() => {
    const grouped: Record<string, typeof availableFields> = {};
    for (const field of availableFields) {
      if (!grouped[field.nodeName]) {
        grouped[field.nodeName] = [];
      }
      grouped[field.nodeName].push(field);
    }
    return grouped;
  }, [availableFields]);

  // Insert expression
  const insertExpression = (expression: string) => {
    onChange(`{{${expression}}}`);
    setFieldBrowserOpen(false);
  };

  // Toggle expression mode
  const toggleExpressionMode = () => {
    if (isExpressionMode && !containsExpression(value)) {
      // Switching back to fixed mode - try to parse as number
      const numValue = parseFloat(String(value));
      if (!isNaN(numValue)) {
        onChange(numValue);
      }
    }
    setIsExpressionMode(!isExpressionMode);
  };

  // Render mode toggle
  const renderModeToggle = () => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant={isExpressionMode ? 'default' : 'ghost'}
            size="sm"
            className={cn(
              'h-6 w-6 p-0',
              isExpressionMode && 'bg-blue-500 hover:bg-blue-600'
            )}
            onClick={toggleExpressionMode}
          >
            {isExpressionMode ? (
              <Code2 className="h-3.5 w-3.5" />
            ) : (
              <Hash className="h-3.5 w-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">
            {isExpressionMode ? 'Switch to fixed value' : 'Switch to expression mode'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Render field browser
  const renderFieldBrowser = () => (
    <Popover open={fieldBrowserOpen} onOpenChange={setFieldBrowserOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 text-xs gap-1"
          disabled={Object.keys(fieldsByNode).length === 0}
        >
          <Sparkles className="h-3 w-3" />
          Insert
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="p-2 border-b">
          <p className="text-xs font-medium">Numeric Fields</p>
          <p className="text-xs text-muted-foreground">Click to insert</p>
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {Object.keys(fieldsByNode).length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              <p>No numeric fields available</p>
              <p className="text-xs mt-1">Run previous nodes first</p>
            </div>
          ) : (
            Object.entries(fieldsByNode).map(([nodeName, fields]) => (
              <Collapsible key={nodeName} defaultOpen={Object.keys(fieldsByNode).length === 1}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 hover:bg-muted rounded text-sm font-medium">
                  <ChevronRight className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-90" />
                  {nodeName}
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {fields.length}
                  </Badge>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="ml-4 space-y-0.5">
                    {fields.slice(0, 15).map((field, idx) => (
                      <button
                        key={`${field.fullPath}-${idx}`}
                        className="w-full text-left px-2 py-1 hover:bg-muted rounded text-xs flex items-center gap-2"
                        onClick={() => insertExpression(field.fullPath)}
                      >
                        <code className="font-mono text-xs flex-1 truncate">{field.path}</code>
                        <span className="text-muted-foreground">{field.value}</span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  // Render preview
  const renderPreview = () => {
    if (!isExpressionMode || !containsExpression(value)) return null;

    return (
      <div className="mt-2 p-2 rounded-md border bg-muted/30">
        <div className="flex items-start gap-2">
          {preview.error ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-destructive mt-0.5" />
              <div className="text-xs text-destructive">{preview.error}</div>
            </>
          ) : preview.value !== null ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5" />
              <div className="text-xs">
                <span className="font-medium">Preview:</span>{' '}
                <span className="font-mono">{String(preview.value)}</span>
              </div>
            </>
          ) : (
            <>
              <Code2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
              <div className="text-xs text-muted-foreground">
                Run upstream nodes to see preview
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label htmlFor={property.key}>
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {property.helpText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{property.helpText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isExpressionMode && containsExpression(value) && (
            <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-600">
              <Code2 className="h-3 w-3 mr-1" />
              Expression
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1">
          {isExpressionMode && renderFieldBrowser()}
          {renderModeToggle()}
        </div>
      </div>

      {property.description && (
        <p className="text-sm text-muted-foreground">{property.description}</p>
      )}

      <Input
        id={property.key}
        type={isExpressionMode ? 'text' : 'number'}
        value={value ?? ''}
        onChange={handleChange}
        placeholder={isExpressionMode
          ? 'e.g., {{ $node["NodeName"].output.count }}'
          : property.placeholder
        }
        min={!isExpressionMode ? property.validation?.min : undefined}
        max={!isExpressionMode ? property.validation?.max : undefined}
        className={cn(
          error ? 'border-destructive' : '',
          isExpressionMode && containsExpression(value) && 'border-blue-500/50 bg-blue-500/5 font-mono text-sm'
        )}
      />

      {renderPreview()}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
